import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet, ActivityIndicator, FlatList, ListRenderItem } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { Event } from '@/types/residents/event';
import DesktopEventCard from '@/components/residents/events/desktop/DesktopEventCard';
import { EmptyState } from '@/components';
import { strings } from '@/constants/residents';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchEvents } from '@/store/slices/residents/homeSlice';
import { selectSortedEvents } from '@/store/selectors/residentsEventsSelectors';

export const DesktopEventsScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((s) => s.home.isLoading);
  const events = useAppSelector(selectSortedEvents);

  React.useEffect(() => {
    dispatch(fetchEvents());
  }, [dispatch]);

  const renderItem: ListRenderItem<Event> = useCallback(({ item }) => {
    return (
      <View style={styles.centerColumn}>
        <DesktopEventCard event={item} />
      </View>
    );
  }, []);

  const keyExtractor = useCallback((item: Event) => String(item.id), []);

  const contentContainerStyle = useMemo(() => styles.content, []);

  if (isLoading) {
    return (
      <View style={[styles.root, styles.loaderContainer]}>
        <ActivityIndicator size="large" color={RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY} />
      </View>
    );
  }

  if (!isLoading && events.length === 0) {
    return (
        <EmptyState title={strings.events.empty.title} description={strings.events.empty.description} iconName="calendar-outline" />
    );
  }

  return (
    <View style={styles.root}>
      <FlatList
        data={events}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View style={styles.itemSeparator} />}
        contentContainerStyle={contentContainerStyle}
        removeClippedSubviews={true}
        windowSize={7}
        initialNumToRender={4}
        maxToRenderPerBatch={6}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  loaderContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: 16,
  },
  centerColumn: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },
  itemSeparator: {
    height: 16,
  },
  loader: {
    marginTop: 24,
  },
  emptyText: {
    textAlign: 'center',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    marginTop: 48,
  },
});

export default DesktopEventsScreen;


